import { LoadingState } from '@playforge/ui';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiClient } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import { onboardingWasCompleted } from '../lib/onboarding-state';
import type { MerchantProfile } from '../lib/types';

const WELCOME_PATH = '/welcome';

/**
 * Redirect first-run merchants to the welcome wizard until onboarding is
 * complete. If the profile can't be loaded (e.g. unauthenticated in dev), the
 * app is not blocked.
 */
export function OnboardingGate({ children }: { children: ReactNode }): JSX.Element {
  const location = useLocation();
  const { data, loading, error } = useApi(
    () => apiClient.get<MerchantProfile>('/merchant/profile'),
    [],
  );

  if (loading) {
    return <LoadingState label="Loading PlayForge" />;
  }

  // Treat onboarding as done if the server says so OR the wizard just completed
  // it this session (the profile fetch doesn't refetch, so without this the gate
  // would bounce a just-finished merchant back to /welcome until a full reload).
  const completed = (data?.onboardingCompleted ?? false) || onboardingWasCompleted();

  // Do not block the app if we couldn't determine onboarding state.
  if (!error && data) {
    if (!completed && location.pathname !== WELCOME_PATH) {
      return <Navigate to={WELCOME_PATH} replace />;
    }
    if (completed && location.pathname === WELCOME_PATH) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
}
